/* eslint-disable @typescript-eslint/ban-ts-comment */
// @ts-nocheck - pre-existing type mismatches in provider slice
import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import _ from 'lodash';

import {
  deleteProviderFailure,
  deleteProviderSuccess,
  getProvider,
  setShouldRefetchProviders,
} from '@/store/slices/provider.slice';
import { RootState } from '@/store/types';
import { Modal } from '@/components/elements/Modal';
import ConfirmActionModal, {
  DeleteIcon,
} from '@/components/organism/ConfirmActionModal';
import { getDateTime } from '@/utils/dateTime';
import { deleteProvider } from '@/services/provider';
import { useToast } from '@/components/organism/Toast/ToastProvider';
import {
  PROVIDER_CONSTANT_PROPS,
  PROVIDER_VIEW_PROPS,
} from '@/constant/provider/provider';
import { tid } from '@/utils/testId';
import { PROVIDER_TEST_IDS as TID } from '@/constant/testIds/provider.testIds';
import { getRole } from '@/constant/users/user.constant';
import { authorizeRole } from '@/constant/global/global.constant';
import { useTheme } from '@/context/theme.context';
import PageToolbar from '@/components/organism/Cards/PageToolbar';
import { PageCard, SectionCard, StatCell, MetaChip } from '@/components';

// ── Atoms ──────────────────────────────────────────────────────────────────
import {
  MdOutlineAccountBalance,
  MdOutlineDevices,
  MdOutlineSync,
  MdOutlineSettings,
  MdOutlineEdit,
  MdOutlineDelete,
  MdOutlineClose,
  MdOutlineCheckCircle,
} from 'react-icons/md';
import { FiGitBranch, FiClipboard } from 'react-icons/fi';
import { getLabelIcon, getSectionIcon } from '@/components/atoms/IconMap';
import { PROVIDER_SERVICE_ICONS } from '@/constant/provider/providerIcons';

// ── Cards ──────────────────────────────────────────────────────────────────
import { FeatureFlagCard, ServiceCard } from '@/components';
import type { FieldRow, ServiceField, SubItem } from '@/components';
import { APP_BG } from '@/constant/theme/colors.constant';
import { PillCell, AppButton } from '../../components/atoms';

// ── VDI Adapters ─────────────────────────────────────────────────────────────
/**
 * Converts a raw VDI service object into a generic FieldRow[] config.
 * This is the ONLY place in the app that knows about VDI_SETTINGS data shape.
 * ServiceCard itself is fully data-agnostic.
 */
const toServiceRows = (service: any): FieldRow[] => {
  if (!service) return [];
  const rows: FieldRow[] = [];

  if (service.AUTH?.HOST !== undefined) {
    rows.push({
      fields: [
        {
          type: 'pill' as const,
          label: 'Host',
          labelIcon: getLabelIcon('Host'),
          value: service.AUTH.HOST,
          mono: true,
          pillConfig: {
            bg: APP_BG.overlay,
            text: 'text-gray-600 dark:text-gray-300',
            border: 'border-gray-200 dark:border-gray-700',
          },
        },
      ],
    });
  }

  const settingsFields: ServiceField[] = [];
  if (service.AUTH?.AUTH_TYPE !== undefined) {
    settingsFields.push({
      type: 'pill',
      label: 'Auth Type',
      labelIcon: getLabelIcon('Auth Type'),
      value: service.AUTH.AUTH_TYPE,
      pillConfig: {
        bg: 'bg-sky-50 dark:bg-sky-950/20',
        text: 'text-sky-700 dark:text-sky-400',
        border: 'border-sky-200 dark:border-sky-800/50',
      },
    });
  }
  if (service.VERSION !== undefined) {
    settingsFields.push({
      type: 'text',
      label: 'Version',
      labelIcon: getLabelIcon('Version'),
      value: service.VERSION || '—',
    });
  }
  if (service.GET_STATE !== undefined) {
    settingsFields.push({
      type: 'pill',
      label: 'Get State',
      labelIcon: getLabelIcon('Get State'),
      value: service.GET_STATE ? 'Enabled' : 'Disabled',
      preset: 'boolEnabled' as const,
    });
  }
  if (settingsFields.length > 0)
    rows.push({ fields: settingsFields, altBg: true });

  if (Array.isArray(service.VDI_TYPES) && service.VDI_TYPES.length > 0) {
    rows.push({
      fields: [
        {
          type: 'pill-list' as const,
          label: 'VDI Types',
          labelIcon: getLabelIcon('VDI Types'),
          values: service.VDI_TYPES,
          pillConfig: {
            bg: 'bg-orange-50 dark:bg-orange-950/20',
            text: 'text-orange-700 dark:text-orange-400',
            border: 'border-orange-200 dark:border-orange-800/50',
          },
        },
      ],
    });
  }

  if (service.VDI_HEADER && typeof service.VDI_HEADER === 'object') {
    const kvFields: ServiceField[] = Object.entries(service.VDI_HEADER).map(
      ([k, v]) => ({
        type: 'kv' as const,
        label: k.replace(/_/g, ' '),
        value: String(v),
      }),
    );
    for (let i = 0; i < kvFields.length; i += 2) {
      rows.push({ fields: kvFields.slice(i, i + 2), altBg: true });
    }
  }

  return rows;
};

/** Converts sub-service keys of a parent VDI service into SubItem[] */
const toSubItems = (service: any, keys: string[]): SubItem[] => {
  if (!service) return [];
  return keys
    .filter((k) => service[k] != null)
    .map((k) => ({
      key: k,
      enabled: !!service[k]?.ENABLED,
      rows: toServiceRows(service[k]),
    }));
};

/* ══════════════════════════════════════════════════════════════════════════
   ViewProvider — main page component
══════════════════════════════════════════════════════════════════════════ */
const ViewProvider = () => {
  const [isShow, setIsShow] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const { colors } = useTheme();

  const dispatch = useDispatch();
  const toast = useToast();
  const { provider } = useSelector((state: RootState) => state.provider);
  const location = useLocation();
  const navigate = useNavigate();
  const { id: paramId } = useParams<{ id: string }>();
  const id = paramId || location?.state?.providerId;
  const selectedProviderId = localStorage.getItem(
    PROVIDER_CONSTANT_PROPS.SELECTED_PROVIDER_ID,
  );
  const userHasWriteAccess = _.includes(authorizeRole.USERS.CREATE, getRole());

  useEffect(() => {
    dispatch(getProvider(id || selectedProviderId));
  }, [id]);

  /** Parse provider.configs — accept both string (JSON) and object forms */
  const cfg = (() => {
    if (!provider?.configs) return null;
    try {
      return typeof provider.configs === 'string'
        ? JSON.parse(provider.configs)
        : provider.configs;
    } catch {
      return provider.configs;
    }
  })();

  const handleDelete = async () => {
    const deleteId = id || provider?.providerId || selectedProviderId;
    setIsDeleting(true);
    try {
      const res = await deleteProvider(deleteId);
      navigate('/providers', { replace: true });
      toast?.pushSuccess(
        res?.data?.message,
        PROVIDER_CONSTANT_PROPS.GENERIC.TOAST_TIME,
      );
      dispatch(deleteProviderSuccess(res?.data));
      dispatch(setShouldRefetchProviders(true));
      setIsShow(false);
    } catch (error) {
      dispatch(deleteProviderFailure());
      toast?.pushError(
        error?.response?.data?.message,
        PROVIDER_CONSTANT_PROPS.GENERIC.TOAST_TIME,
      );
      setIsShow(false);
    } finally {
      setIsDeleting(false);
    }
  };

  /** React-icon references for the service cards */
  const icons = {
    mms: <PROVIDER_SERVICE_ICONS.MMS className="w-4 h-4" />,
    vms: <PROVIDER_SERVICE_ICONS.VMS className="w-4 h-4" />,
    clients: <PROVIDER_SERVICE_ICONS.CLIENTS className="w-4 h-4" />,
    alerts: <PROVIDER_SERVICE_ICONS.ALERTS className="w-4 h-4" />,
    coolers: <PROVIDER_SERVICE_ICONS.SMART_COOLERS className="w-4 h-4" />,
  };

  return (
    <>
      {/* ── Outer Card ── */}
      <PageCard testId={tid(TID.VIEW_PAGE)}>
        {/* ── Header ── */}
        <PageToolbar
          icon={<MdOutlineAccountBalance className="w-5 h-5" />}
          title={provider?.profile || PROVIDER_VIEW_PROPS.FALLBACK_TITLE}
          pills={
            <>
              {provider?.partnerId && (
                <MetaChip mono>ID: {provider.partnerId}</MetaChip>
              )}
              {provider?.name && <MetaChip>{provider.name}</MetaChip>}
            </>
          }
          actionButtons={
            <div className="flex items-center gap-1.5 sm:gap-2">
              {userHasWriteAccess && (
                <>
                  <AppButton
                    data-testid={tid(TID.VIEW_EDIT_BTN)}
                    variant="neutral"
                    size="sm"
                    icon={<MdOutlineEdit className="w-3.5 h-3.5" />}
                    onClick={() =>
                      navigate(`/providers/edit/${provider?.providerId}`, {
                        state: provider,
                      })
                    }
                  >
                    {PROVIDER_VIEW_PROPS.ACTIONS.EDIT}
                  </AppButton>
                  <AppButton
                    data-testid={tid(TID.VIEW_DELETE_BTN)}
                    variant="danger"
                    tint
                    size="sm"
                    icon={<DeleteIcon />}
                    onClick={() => setIsShow(true)}
                  >
                    {PROVIDER_VIEW_PROPS.ACTIONS.DELETE}
                  </AppButton>
                </>
              )}
              <AppButton
                data-testid={tid(TID.VIEW_BACK_BTN)}
                variant="neutral"
                shape="square"
                size="md"
                type="button"
                onClick={() => navigate('/providers', { replace: true })}
                aria-label="Close"
                className="hover:rotate-90 transition-transform duration-200"
                icon={<MdOutlineClose className="w-4 h-4" />}
              />
            </div>
          }
        />

        {/* ── Card Body ── */}
        <div
          className={`p-3 sm:p-6 ${APP_BG.base} transition-colors duration-300 space-y-5`}
        >
          {/* ════ PROVIDER DETAILS ════ */}
          <SectionCard
            icon={getSectionIcon(PROVIDER_VIEW_PROPS.SECTIONS.DETAILS_TITLE)}
            title={PROVIDER_VIEW_PROPS.SECTIONS.DETAILS_TITLE}
            subtitle={PROVIDER_VIEW_PROPS.SECTIONS.DETAILS_SUBTITLE}
            bodyClassName=""
          >
            <div
              className={`${APP_BG.base} divide-y divide-gray-100 dark:divide-gray-700/60`}
            >
              {/* ── Row 1 : Identifiers ── */}
              <div className="grid grid-cols-2 sm:grid-cols-5 divide-x-0 sm:divide-x divide-gray-100 dark:divide-gray-700/60">
                <StatCell
                  size="md"
                  labelIcon={getLabelIcon(PROVIDER_VIEW_PROPS.FIELDS.NAME)}
                  label={PROVIDER_VIEW_PROPS.FIELDS.NAME}
                  value={provider?.name}
                />
                <StatCell
                  size="md"
                  mono
                  labelIcon={getLabelIcon(
                    PROVIDER_VIEW_PROPS.FIELDS.PROVIDER_ID,
                  )}
                  label={PROVIDER_VIEW_PROPS.FIELDS.PROVIDER_ID}
                  value={provider?.providerId}
                />
                <StatCell
                  size="md"
                  mono
                  labelIcon={getLabelIcon(
                    PROVIDER_VIEW_PROPS.FIELDS.PARTNER_ID,
                  )}
                  label={PROVIDER_VIEW_PROPS.FIELDS.PARTNER_ID}
                  value={provider?.partnerId}
                />
                <StatCell
                  size="md"
                  labelIcon={getLabelIcon(PROVIDER_VIEW_PROPS.FIELDS.SERVER)}
                  label={PROVIDER_VIEW_PROPS.FIELDS.SERVER}
                  value={
                    provider?.server ? (
                      <PillCell
                        value={provider.server}
                        shape="badge"
                        config={{
                          bg: 'bg-gray-100 dark:bg-gray-800',
                          text: 'text-gray-600 dark:text-gray-300',
                          border: 'border-gray-200 dark:border-gray-700',
                        }}
                      />
                    ) : undefined
                  }
                />
                <StatCell
                  size="md"
                  labelIcon={getLabelIcon(PROVIDER_VIEW_PROPS.FIELDS.PROFILE)}
                  label={PROVIDER_VIEW_PROPS.FIELDS.PROFILE}
                  value={
                    provider?.profile ? (
                      <PillCell
                        value={provider.profile}
                        shape="badge"
                        config={{
                          bg: 'bg-orange-50 dark:bg-orange-950/20',
                          text: 'text-orange-700 dark:text-orange-400',
                          border: 'border-orange-200 dark:border-orange-800/50',
                        }}
                      />
                    ) : undefined
                  }
                />
              </div>

              {/* ── Row 2 : VDI & Timestamps ── */}
              <div
                className={`grid grid-cols-2 sm:grid-cols-4 divide-x-0 sm:divide-x divide-gray-100 dark:divide-gray-700/60 ${APP_BG.overlay}`}
              >
                <StatCell
                  size="md"
                  labelIcon={getLabelIcon(
                    PROVIDER_VIEW_PROPS.FIELDS.VDI_VERSION,
                  )}
                  label={PROVIDER_VIEW_PROPS.FIELDS.VDI_VERSION}
                  value={
                    provider?.vdiVersion ? (
                      <PillCell
                        value={`v${provider.vdiVersion}`}
                        shape="badge"
                        config={{
                          bg: 'bg-sky-50 dark:bg-sky-900/20',
                          text: 'text-sky-700 dark:text-sky-400',
                          border: 'border-sky-200 dark:border-sky-800/50',
                        }}
                      />
                    ) : undefined
                  }
                />
                <StatCell
                  size="md"
                  labelIcon={getLabelIcon(PROVIDER_VIEW_PROPS.FIELDS.VDI_SYNC)}
                  label={PROVIDER_VIEW_PROPS.FIELDS.VDI_SYNC}
                  value={
                    <PillCell
                      value={provider?.syncVdi ? 'Success' : 'Failure'}
                      preset="vdiSync"
                      dot
                      size="text-xs"
                      customStyle={{ padding: '0.25rem 0.625rem' }}
                    />
                  }
                />
                <StatCell
                  size="md"
                  labelIcon={getLabelIcon(
                    PROVIDER_VIEW_PROPS.FIELDS.CREATED_ON,
                  )}
                  label={PROVIDER_VIEW_PROPS.FIELDS.CREATED_ON}
                  value={getDateTime(provider?.createdOn)}
                />
                <StatCell
                  size="md"
                  labelIcon={getLabelIcon(
                    PROVIDER_VIEW_PROPS.FIELDS.LAST_UPDATED,
                  )}
                  label={PROVIDER_VIEW_PROPS.FIELDS.LAST_UPDATED}
                  value={getDateTime(provider?.updatedOn)}
                />
              </div>
            </div>
          </SectionCard>

          <SectionCard
            icon={getSectionIcon(PROVIDER_VIEW_PROPS.SECTIONS.CONFIG_TITLE)}
            title={PROVIDER_VIEW_PROPS.SECTIONS.CONFIG_TITLE}
            subtitle={PROVIDER_VIEW_PROPS.SECTIONS.CONFIG_SUBTITLE}
            bodyClassName=""
          >
            {cfg ? (
              <div
                className={`px-3 py-3 sm:px-5 sm:py-5 ${APP_BG.base} space-y-4`}
              >
                {/* Services — 3-col responsive grid */}
                <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                  <ServiceCard
                    label={PROVIDER_VIEW_PROPS.SERVICES.MMS}
                    icon={icons.mms}
                    enabled={!!cfg?.VDI_SETTINGS?.MMS?.ENABLED}
                    rows={toServiceRows(cfg?.VDI_SETTINGS?.MMS)}
                  />
                  <ServiceCard
                    label={PROVIDER_VIEW_PROPS.SERVICES.VMS}
                    icon={icons.vms}
                    enabled={!!cfg?.VDI_SETTINGS?.VMS?.ENABLED}
                    rows={toServiceRows(cfg?.VDI_SETTINGS?.VMS)}
                  />
                  <ServiceCard
                    label={PROVIDER_VIEW_PROPS.SERVICES.CLIENTS}
                    icon={icons.clients}
                    enabled={!!cfg?.VDI_SETTINGS?.CLIENTS?.ENABLED}
                    rows={toServiceRows(cfg?.VDI_SETTINGS?.CLIENTS)}
                  />
                  <ServiceCard
                    label={PROVIDER_VIEW_PROPS.SERVICES.ALERTS}
                    icon={icons.alerts}
                    enabled={!!cfg?.VDI_SETTINGS?.ALERTS?.ENABLED}
                    subItems={toSubItems(cfg?.VDI_SETTINGS?.ALERTS, [
                      'MMS',
                      'VMS',
                      'CLIENTS',
                      'GATEWAY',
                    ])}
                  />
                  <ServiceCard
                    label={PROVIDER_VIEW_PROPS.SERVICES.SMART_COOLERS}
                    icon={icons.coolers}
                    enabled={!!cfg?.VDI_SETTINGS?.SMART_COOLERS?.ENABLED}
                    subItems={toSubItems(cfg?.VDI_SETTINGS?.SMART_COOLERS, [
                      'MMS',
                      'VMS',
                      'CLIENTS',
                      'GATEWAY',
                    ])}
                    enabledBorderClass="border-emerald-200/80 dark:border-emerald-900/40"
                    enabledHeaderBgClass="bg-emerald-50/70 dark:bg-emerald-950/15"
                  />

                  <div className="grid gap-4">
                    <FeatureFlagCard
                      label={
                        PROVIDER_VIEW_PROPS.FEATURES.FORK_TRANSACTION_LABEL
                      }
                      desc={PROVIDER_VIEW_PROPS.FEATURES.FORK_TRANSACTION_DESC}
                      enabled={cfg?.VDI_SETTINGS?.IS_FORK_TRANSACTION_ENABLED}
                      icon={<FiGitBranch className="w-4 h-4" />}
                    />
                    <FeatureFlagCard
                      label={PROVIDER_VIEW_PROPS.FEATURES.RECONCILIATION_LABEL}
                      desc={PROVIDER_VIEW_PROPS.FEATURES.RECONCILIATION_DESC}
                      enabled={cfg?.RECONCILIATION?.ENABLED}
                      icon={<FiClipboard className="w-4 h-4" />}
                    />
                  </div>
                </div>
              </div>
            ) : (
              <div
                className={`flex flex-col items-center justify-center py-20 px-6 text-center ${APP_BG.overlay}`}
              >
                <div
                  className={`w-14 h-14 rounded-xl ${APP_BG.muted} flex items-center justify-center mb-3`}
                >
                  <FiClipboard className="w-7 h-7 text-gray-300 dark:text-gray-500" />
                </div>
                <p className="text-sm font-semibold text-gray-500 dark:text-gray-400">
                  {PROVIDER_VIEW_PROPS.EMPTY_STATE.TITLE}
                </p>
                <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">
                  {PROVIDER_VIEW_PROPS.EMPTY_STATE.SUBTITLE}
                </p>
              </div>
            )}
          </SectionCard>
        </div>
      </PageCard>

      {/* ── Delete Confirmation Modal ── */}
      <ConfirmActionModal
        isOpen={isShow}
        onClose={() => setIsShow(false)}
        onConfirm={handleDelete}
        title={PROVIDER_VIEW_PROPS.DELETE_MODAL.TITLE}
        subtitle={PROVIDER_VIEW_PROPS.DELETE_MODAL.SUBTITLE}
        description={
          <>
            {PROVIDER_VIEW_PROPS.DELETE_MODAL.BODY_PREFIX}{' '}
            <span className="font-semibold text-gray-900 dark:text-white">
              {provider?.name}
            </span>
            {PROVIDER_VIEW_PROPS.DELETE_MODAL.BODY_SUFFIX}
          </>
        }
        confirmWord={PROVIDER_CONSTANT_PROPS.GENERIC.DELETE}
        placeholder={PROVIDER_VIEW_PROPS.DELETE_MODAL.INPUT_PLACEHOLDER}
        inputLabel={PROVIDER_VIEW_PROPS.DELETE_MODAL.INPUT_LABEL}
        hint={PROVIDER_VIEW_PROPS.DELETE_MODAL.HINT_SUFFIX}
        confirmLabel={PROVIDER_VIEW_PROPS.DELETE_MODAL.CONFIRM_LABEL}
        confirmIcon={<DeleteIcon />}
        variant="danger"
        size="xl"
        icon={<DeleteIcon />}
        loading={isDeleting}
      />
    </>
  );
};

export default ViewProvider;
